import type { BillingPeriod, CatalogueItemDto, EstimateDto, EstimateLineDto } from "@/shared/api/types";
import {
  capacityItems,
  capacityMix,
  peoplePerUnit,
  type CapacityMix,
  type MixUnit,
} from "@/shared/utils/capacity";
import { formatCount } from "@/shared/utils/numbers";
import {
  lineFor,
  peopleAllowance,
  quantityOf,
  sellable,
  terminalCount,
  unitCount,
  unitPaise,
  unitPricePresentation,
  withQuantity,
  type ConfiguratorMode,
  type PlanConfiguration,
} from "./plan-pricing";

/**
 * The "Terminals and people" card's arithmetic (M23 §4.1, decision D6 — Kumar, 2026-10-05: "needs-based").
 *
 * The customer says how many TERMINALS and how many PEOPLE; the three capacity items — Access Control,
 * Extra terminal, Extra people — are the answer, chosen by `capacityMix` (`utils/capacity.ts`, the server's
 * own function mirrored) and shown read-only. Three free steppers would let a customer pay for 750 people
 * to get three doors, which is the mistake the milestone exists to remove; this file is the half that turns
 * two numbers into quantities and back again. Pure and unit-tested, like `plan-pricing.ts`; amounts are in
 * PAISE until `formatMoney` puts them on screen.
 */

/** The three capacity items the configurator may BUY: on sale (`sellable`) and, for the two add-ons, priced. */
export interface CapacityOffer {
  base: CatalogueItemDto | null;
  /** Extra terminal; null while it is a draft or has no price — then every terminal is Access Control. */
  terminal: CatalogueItemDto | null;
  people: CatalogueItemDto | null;
}

export function capacityOffer(catalogue: readonly CatalogueItemDto[]): CapacityOffer {
  const found = capacityItems(sellable(catalogue));
  const priced = (item: CatalogueItemDto | null) => (item?.price ? item : null);
  return { base: found.base, terminal: priced(found.terminal), people: priced(found.people) };
}

/**
 * What `useCapacityUnitQuote` asks the estimate for: each capacity item on sale with a price, at the least
 * quantity the api accepts for it (its minimum, at least one). Quantity one for every item made the estimate
 * refuse the WHOLE quote when Access Control is sold two at a time, and the mix fell back to list prices,
 * missing the discounts it exists for (third review, 2026-10-05). The answer is read per unit
 * (`unitPaise`), so the quantity asked for never changes the price.
 */
export function capacityQuoteItems(
  catalogue: readonly CatalogueItemDto[],
): Array<{ code: string; quantity: number }> {
  const offer = capacityOffer(catalogue);
  return [offer.base, offer.terminal, offer.people]
    .filter((item): item is CatalogueItemDto => item?.price != null)
    .map((item) => ({ code: item.code, quantity: Math.max(1, item.minQuantity) }));
}

/** The two numbers the customer is asked for. */
export interface CapacityNeeds {
  terminals: number;
  people: number;
}

/** What a configuration already provides: every terminal item's quantity, and the people they all add. */
export function capacityNeeds(
  catalogue: readonly CatalogueItemDto[],
  config: PlanConfiguration,
): CapacityNeeds {
  return { terminals: terminalCount(catalogue, config), people: peopleAllowance(catalogue, config) };
}

/** The three quantities a configuration holds — what a mix sets, and what "untouched" is judged on. */
export interface CapacityQuantities {
  base: number;
  terminal: number;
  peopleLot: number;
}

export function capacityQuantities(offer: CapacityOffer, config: PlanConfiguration): CapacityQuantities {
  const of = (item: CatalogueItemDto | null) => (item ? quantityOf(config.items, item.code) : 0);
  return { base: of(offer.base), terminal: of(offer.terminal), peopleLot: of(offer.people) };
}

export const sameQuantities = (a: CapacityQuantities, b: CapacityQuantities): boolean =>
  a.base === b.base && a.terminal === b.terminal && a.peopleLot === b.peopleLot;

/**
 * Where the card reads what ONE unit of a capacity item costs this organisation.
 *
 * `unitQuote` first (`useCapacityUnitQuote`): the api's estimate of one unit of EACH of the three items, so
 * it carries the organisation's own price AND the discount rule that applies to every one of them — including
 * the item the draft does not hold yet, which is exactly the item the mix is deciding whether to buy. The
 * draft's own `estimate` second: it prices only what the draft holds, but it is on screen first. The catalogue
 * last. A rule can cover one item and not another (`catalog_discounts.item_code`), so weighing list prices
 * would answer with a mix that is not the cheapest one the customer is actually charged for.
 */
export interface CapacityPrices {
  estimate?: EstimateDto | undefined;
  unitQuote?: EstimateDto | undefined;
}

/** The api's line for one unit of `item` at `period`, from the first source that priced it; a source for the other period is stale. */
function capacityLineFor(
  item: CatalogueItemDto,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
): EstimateLineDto | undefined {
  for (const source of [prices?.unitQuote, prices?.estimate]) {
    const line = source?.period === period ? lineFor(source, item.code) : undefined;
    if (line) return line;
  }
  return undefined;
}

/**
 * One unit for a whole period, as the mix weighs it: the CHARGED price — after the organisation's own price
 * and the discount the api applies to that item — else the catalogue's list price (`CapacityPrices`).
 */
export function capacityUnitPaise(
  item: CatalogueItemDto,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
): number | null {
  const line = capacityLineFor(item, period, prices);
  return line ? line.unitPaise : unitPaise(item, period);
}

/**
 * One unit a month as the card shows it — what the add-on rows show too (`unitPricePresentation`): the
 * charged price, with the list price struck out when a discount applied. A stale estimate for the other
 * period is ignored, so a toggle never shows a yearly line divided as if it were monthly.
 */
export function capacityPerMonthPaise(
  item: CatalogueItemDto,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
): number | null {
  return unitPricePresentation(item, period, capacityLineFor(item, period, prices)).perMonthPaise;
}

/**
 * The most of `item` a mix may buy: its trial maximum at signup (the api trims a trial to it), else the
 * catalogue's `maxQuantity` (the api refuses more). Null = no ceiling.
 */
function mixCeiling(item: CatalogueItemDto, mode: ConfiguratorMode): number | null {
  return mode === "signup" ? item.trialMaxQuantity : item.maxQuantity;
}

/** What `capacityMix` needs of one item: what a unit adds, what it costs, and how few or many may be bought. */
function mixUnit(
  item: CatalogueItemDto,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
  mode: ConfiguratorMode,
): MixUnit {
  return {
    people: peoplePerUnit(item),
    unitPaise: capacityUnitPaise(item, period, prices),
    max: mixCeiling(item, mode),
    min: item.minQuantity,
  };
}

/**
 * The cheapest mix for `needs` at this period's prices, never fewer of an item than its minimum nor more
 * than `mode` lets it buy (`mixUnit`). A base plan with no price yet still answers in quantities — priced at nothing it simply
 * buys base units — because the card must work before Kumar has priced anything; null when no base plan is
 * on sale at all, or when no mix fits the ceilings.
 */
export function mixFor(
  catalogue: readonly CatalogueItemDto[],
  needs: CapacityNeeds,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
  {
    offer = capacityOffer(catalogue),
    mode = "change",
  }: { offer?: CapacityOffer; mode?: ConfiguratorMode } = {},
): CapacityMix | null {
  if (!offer.base) return null;
  const unit = (item: CatalogueItemDto | null): MixUnit | null =>
    item ? mixUnit(item, period, prices, mode) : null;
  const base = mixUnit(offer.base, period, prices, mode);
  return capacityMix({
    terminals: needs.terminals,
    people: needs.people,
    base: { ...base, unitPaise: base.unitPaise ?? 0 },
    terminal: unit(offer.terminal),
    peopleLot: unit(offer.people),
  });
}

/** The configuration with the mix's quantities: `access_control = b`, `extra_terminal = e`, `people_lot = l`. */
export function withMix(
  catalogue: readonly CatalogueItemDto[],
  config: PlanConfiguration,
  mix: CapacityMix,
): PlanConfiguration {
  const offer = capacityOffer(catalogue);
  let next = config;
  if (offer.base) next = withQuantity(next, offer.base.code, mix.base);
  if (offer.terminal) next = withQuantity(next, offer.terminal.code, mix.terminal);
  if (offer.people) next = withQuantity(next, offer.people.code, mix.peopleLot);
  return next;
}

/**
 * The Terminals stepper's range. At least the base plan's minimum (one: the first terminal is always Access
 * Control). At signup ALL terminals together stop at Access Control's trial maximum — the api's
 * `withinTrialTerminals` (decision D4): an Extra terminal is the same hardware without its 250 people, so
 * the ten terminals a trial may run stay ten however they are split. Otherwise the catalogue's maxima.
 */
export function terminalBounds(
  catalogue: readonly CatalogueItemDto[],
  mode: ConfiguratorMode,
): { min: number; max: number } {
  const { base, terminal } = capacityOffer(catalogue);
  if (!base) return { min: 0, max: 0 };
  const min = Math.max(1, base.minQuantity);
  const max =
    mode === "signup"
      ? base.trialMaxQuantity
      : Math.min(9999, (base.maxQuantity ?? 9999) + (terminal ? (terminal.maxQuantity ?? 9999) : 0));
  return { min, max: Math.max(min, max) };
}

/**
 * The People stepper: in steps of one lot (250), never below what the first terminal brings (250), and never
 * above what the items may hold beside `terminals` — Access Control covers up to one 250 per terminal and
 * Extra people the rest, each up to its own maximum (its trial maximum at signup, which the api would
 * otherwise cut the lots down to without a word). With no Extra people on sale, people come from Access
 * Control alone, up to its own maximum.
 */
export function peopleBounds(
  catalogue: readonly CatalogueItemDto[],
  mode: ConfiguratorMode,
  terminals: number,
): { min: number; max: number; step: number } {
  const { base, people } = capacityOffer(catalogue);
  const perBase = base ? peoplePerUnit(base) : 0;
  const step = Math.max(1, people ? peoplePerUnit(people) : perBase);
  const min = Math.max(perBase, step);
  const cap = (item: CatalogueItemDto) =>
    mode === "signup" ? item.trialMaxQuantity : Math.min(9999, item.maxQuantity ?? 9999);
  const max = people ? terminals * perBase + cap(people) * step : base ? cap(base) * perBase : min;
  return { min, max: Math.max(min, max), step };
}

/**
 * What one unit adds, as the chips every capacity row wears: "+1 terminal", "+250 people". Read off the
 * catalogue (`unitKind`, `includes`, `unitSize`), so a changed lot size needs no code change.
 */
export function capacityAdds(item: Pick<CatalogueItemDto, "unitKind" | "unitSize" | "includes">): string[] {
  const adds: string[] = [];
  if (item.unitKind === "terminal") adds.push(`+${formatCount(item.unitSize)} terminal`);
  const people = peoplePerUnit(item);
  if (people > 0) adds.push(`+${formatCount(people)} people`);
  return adds;
}

/** One line of "What you will have". */
export interface CapacityLine {
  item: CatalogueItemDto;
  role: keyof CapacityOffer;
  quantity: number;
  adds: string[];
  /** One unit a month at the chosen period (`capacityPerMonthPaise`); null while it has no price. */
  perMonthPaise: number | null;
  /** Why this line is (or is not) in the answer — "your first terminal and 250 people", "not needed". */
  why: string;
}

function whyOf(role: keyof CapacityOffer, quantity: number, item: CatalogueItemDto): string {
  if (role === "base") {
    const people = formatCount(peoplePerUnit(item));
    return quantity === 1
      ? `your first terminal and ${people} people`
      : `a terminal and ${people} people each`;
  }
  if (quantity === 0) return "not needed";
  // M24 (plan §15): an Extra terminal slot holds a Dahua terminal or a Face Kiosk phone.
  return role === "terminal"
    ? "one more terminal or Face Kiosk phone — no people"
    : "more people, no more doors";
}

/**
 * What one unit of each capacity item costs at `period`, as one string: the card re-runs the mix of needs the
 * owner set when it changes — a unit quote that arrives after they typed, or a switch of period — because the
 * quantities picked at the old prices may not be the cheapest any more (review finding, 2026-10-05).
 */
export function capacityPriceKey(
  catalogue: readonly CatalogueItemDto[],
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
): string {
  const offer = capacityOffer(catalogue);
  return [
    period,
    ...(["base", "terminal", "people"] as const).map((role) => {
      const item = offer[role];
      return item ? `${item.code}=${capacityUnitPaise(item, period, prices) ?? "-"}` : "-";
    }),
  ].join(":");
}

/**
 * The read-only answer, always in the group's order — Access Control → Extra terminal → Extra people — with
 * what each adds and why it is there. An Extra terminal that is not on sale is not a line at all: every
 * terminal is Access Control, as it was before M23.
 */
export function capacityLines(
  catalogue: readonly CatalogueItemDto[],
  config: PlanConfiguration,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
): CapacityLine[] {
  const offer = capacityOffer(catalogue);
  return (["base", "terminal", "people"] as const).flatMap((role): CapacityLine[] => {
    const item = offer[role];
    if (!item) return [];
    const quantity = quantityOf(config.items, item.code);
    return [
      {
        item,
        role,
        quantity,
        adds: capacityAdds(item),
        perMonthPaise: capacityPerMonthPaise(item, period, prices),
        why: whyOf(role, quantity, item),
      },
    ];
  });
}

export interface CapacitySummary {
  terminals: number;
  people: number;
  /** Σ quantity × the per-month price each line shows. */
  perMonthPaise: number;
  /**
   * What the same terminals and people would cost a month WITHOUT Extra terminals, less this — "Saves ₹200
   * a month against 3 × Access Control". Null when nothing is saved (no Extra terminal in the mix).
   */
  saving: { perMonthPaise: number; against: string } | null;
}

/** The footer of the card: what the lines add up to, and the saving the Extra terminals make. */
export function capacitySummary(
  catalogue: readonly CatalogueItemDto[],
  config: PlanConfiguration,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
): CapacitySummary {
  const lines = capacityLines(catalogue, config, period, prices);
  const cost = (quantity: number, line: CapacityLine | undefined) => quantity * (line?.perMonthPaise ?? 0);
  const byRole = (role: keyof CapacityOffer) => lines.find((l) => l.role === role);
  const perMonthPaise = lines.reduce((sum, line) => sum + cost(line.quantity, line), 0);
  const needs = capacityNeeds(catalogue, config);
  const offer = capacityOffer(catalogue);
  const extra = byRole("terminal");
  let saving: CapacitySummary["saving"] = null;
  if (offer.base && extra && extra.quantity > 0) {
    const without = mixFor(catalogue, needs, period, prices, { offer: { ...offer, terminal: null } });
    if (without) {
      const other = cost(without.base, byRole("base")) + cost(without.peopleLot, byRole("people"));
      const against =
        without.peopleLot === 0
          ? `${formatCount(without.base)} × ${offer.base.name}`
          : `${offer.base.name} and ${offer.people?.name ?? "Extra people"} alone`;
      if (other > perMonthPaise) saving = { perMonthPaise: other - perMonthPaise, against };
    }
  }
  return { terminals: needs.terminals, people: needs.people, perMonthPaise, saving };
}

/** "1 Access Control + 2 Extra terminals + 1 × lot of 250 people" — a mix in the words of its lines. */
export function mixWords(offer: CapacityOffer, mix: CapacityMix): string {
  const parts: string[] = [];
  if (offer.base && mix.base > 0) parts.push(`${formatCount(mix.base)} ${offer.base.name}`);
  if (offer.terminal && mix.terminal > 0) {
    const name = offer.terminal.name;
    parts.push(
      `${formatCount(mix.terminal)} ${mix.terminal === 1 || name.endsWith("s") ? name : `${name}s`}`,
    );
  }
  if (offer.people && mix.peopleLot > 0) parts.push(unitCount(mix.peopleLot, offer.people.unitLabel));
  return parts.join(" + ");
}

/** D5's suggestion: the cheaper mix, the needs it answers, and what it saves a month. */
export interface CheaperMix {
  mix: CapacityMix;
  needs: CapacityNeeds;
  savingPerMonthPaise: number;
}

/**
 * Decision D5 (Kumar, 2026-10-05): an organisation whose held mix costs more than the cheapest one for the
 * same needs is told so ONCE, inside the configurator, and nowhere else — never a banner, never a push.
 *
 * "The same needs" are the terminals it holds and the people it actually has: what is on file
 * (`peopleInUse`) or held by Staff and Student Attendance (`personQuantity`, which may not exceed people
 * capacity, rule 2), rounded up to whole lots by the mix. Not the people CAPACITY it holds — every customer
 * before M23 holds Access Control alone, whose capacity the same items reproduce exactly, so a capacity-based
 * comparison could never find Kumar's case: three Access Control for 48 people, which is one Access Control
 * and two Extra terminals for ₹400 a month less at his prices.
 *
 * Only while the capacity in the draft is what is held (`held`): once the owner has set Terminals or People
 * themselves, the card's own mix is the answer to what they asked, and repeating a suggestion beside it
 * would be arguing with them. Null when the held mix is already the cheapest.
 */
export function cheaperThanHeld(
  catalogue: readonly CatalogueItemDto[],
  held: PlanConfiguration,
  config: PlanConfiguration,
  peopleInUse: number,
  personQuantity: number,
  period: BillingPeriod,
  prices: CapacityPrices | undefined,
): CheaperMix | null {
  const offer = capacityOffer(catalogue);
  const holding = capacityQuantities(offer, config);
  if (!offer.base || !sameQuantities(holding, capacityQuantities(offer, held))) return null;
  const terminals = terminalCount(catalogue, config);
  const mix = mixFor(
    catalogue,
    { terminals, people: Math.max(peopleInUse, personQuantity, 1) },
    period,
    prices,
  );
  if (!mix || sameQuantities(holding, { base: mix.base, terminal: mix.terminal, peopleLot: mix.peopleLot }))
    return null;
  const unit = (item: CatalogueItemDto | null) => (item ? (capacityUnitPaise(item, period, prices) ?? 0) : 0);
  const heldCost =
    holding.base * unit(offer.base) +
    holding.terminal * unit(offer.terminal) +
    holding.peopleLot * unit(offer.people);
  if (mix.costPaise >= heldCost) return null;
  const divisor = period === "yearly" ? 12 : 1;
  return {
    mix,
    needs: { terminals, people: mix.people },
    savingPerMonthPaise: Math.round((heldCost - mix.costPaise) / divisor),
  };
}
